<script lang="ts" setup generic="TSelection extends string | number | boolean | object | Record<string, any>">
import { computed, provide, ref } from 'vue'
import { Combobox } from '@headlessui/vue'
import { DTFieldComboboxProvider } from '../../keys'
import DTFieldComboboxInput from './DTFieldComboboxInput.vue'

type OptionValue = TSelection extends Array<infer TIndex> ? TIndex : TSelection

interface Option {
  text: string
  value: OptionValue
}

const props = defineProps<{
  modelValue?: string
  placeholder?: string
  multiple?: boolean
  selection?: TSelection
  options?: Option[]
  required?: boolean
  disabled?: boolean
  compareOptionsBy?: InstanceType<typeof Combobox>['$props']['by']
}>()

const emit = defineEmits<{
  (e: 'update:selection', value: TSelection): void
  (e: 'update:modelValue', value: string): void
}>()

const trigger = ref<InstanceType<typeof DTFieldComboboxInput>>()

provide(DTFieldComboboxProvider, {
  trigger: () => trigger.value?.$el,
})

const query = ref('')
const queryHandler = computed({
  get() {
    if (props.modelValue !== undefined)
      return props.modelValue

    return query.value
  },
  set(value) {
    query.value = value
    emit('update:modelValue', value)
  },
})

const selectionHandler = computed({
  get() {
    return props.selection
  },
  set(value: any) {
    emit('update:selection', value as TSelection)
  },
})

const filteredOptions = computed(() =>
  !queryHandler.value
    ? props.options
    : props.options?.filter(option => option.text.toLowerCase().includes(queryHandler.value.toLowerCase())),
)
</script>

<template>
  <div class="flex flex-col">
    <Combobox
      v-model="selectionHandler"
      :multiple="multiple"
      :by="compareOptionsBy"
      :disabled
    >
      <DTFieldComboboxInput
        ref="trigger"
        v-model="queryHandler"
        class="grow"
        :placeholder
        :required
        :disabled
      >
        <slot name="input" />
      </DTFieldComboboxInput>
      <slot>
        <DTFieldComboboxOptions>
          <DTFieldComboboxOption
            v-for="(option, index) in filteredOptions"
            :key="`combobox-option-${index}`"
            :value="option.value"
            :text="option.text"
          />
        </DTFieldComboboxOptions>
      </slot>
    </Combobox>
  </div>
</template>
